import type { ComponentProps } from 'react';

import type { Badge } from '@/components/ui';
import i18n, { currentLocale, intlLocale } from '@/lib/i18n';
import { formatMinor, planKeySchema, type PlanKey } from '@/lib/plan-catalog';
import type { TradeKey } from '@/lib/schemas';
import { TRADES } from '@/lib/trade';

import { FEATURE_KEYS, type FeatureKey, type SubscriptionStatus } from './admin-schemas';

/* תוויות לערכים שמגיעים מהשרת כמחרוזות. ערך לא מוכר מוצג כפי שהוא. */

export function tradeLabel(trade: string | null): string {
  if (trade && trade in TRADES) return TRADES[trade as TradeKey].label;
  return trade && trade !== 'UNSET' ? trade : i18n.t('admin:overview.unsetTrade');
}

export function langLabel(lang: string): string {
  if (lang === 'he') return i18n.t('admin:lang.he');
  if (lang === 'en') return i18n.t('admin:lang.en');
  return lang;
}

export function screenLabel(screen: string): string {
  if (screen === 'sm') return i18n.t('admin:screen.sm');
  if (screen === 'md') return i18n.t('admin:screen.md');
  if (screen === 'lg') return i18n.t('admin:screen.lg');
  return screen;
}

const isFeature = (k: string): k is FeatureKey => (FEATURE_KEYS as readonly string[]).includes(k);

export function featureLabel(feature: string): string {
  return isFeature(feature) ? i18n.t(`admin:features.${feature}`) : feature;
}

/** תאריך יום (YYYY-MM-DD, לפי שעון ישראל בשרת) → "8 באוק׳" / "Oct 8". */
export function formatDay(day: string): string {
  const [y, m, d] = day.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return day;
  return new Intl.DateTimeFormat(intlLocale(), {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(y, m - 1, d)));
}

/** שם תוכנית (i18n) — ערך לא מוכר מוצג כפי שהוא. */
export function planLabel(plan: string): string {
  return isPlan(plan) ? i18n.t(`admin:billing.plans.${plan}`) : plan;
}

const isPlan = (k: string): k is PlanKey =>
  (planKeySchema.options as readonly string[]).includes(k);

/** צבע לסטטוס מנוי. */
export function subscriptionTone(
  status: SubscriptionStatus,
  complimentary = false,
): NonNullable<ComponentProps<typeof Badge>['tone']> {
  if (complimentary && status === 'ACTIVE') return 'violet';
  return (
    {
      ACTIVE: 'success',
      TRIALING: 'info',
      PAST_DUE: 'warning',
      CANCELED: 'danger',
      EXPIRED: 'neutral',
    } as const
  )[status];
}

/** אגורות → ₪ בשפת הממשק (לפני מע"מ). */
export function money(minor: number): string {
  return formatMinor(minor, currentLocale());
}
